import React from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';

export const MotionSection = ({ children, delay = 0, y = 20 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const MotionHoverCard = ({ children }) => (
  <motion.div whileHover={{ y: -4, transition: { duration: 0.2 } }} whileTap={{ scale: 0.995 }}>
    {children}
  </motion.div>
);

export const ModernPageLoader = ({ label = 'Loading content...' }) => (
  <Box
    sx={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 132,
      width: '100%',
      overflow: 'hidden',
      px: 1.2,
    }}
  >
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        background:
          'radial-gradient(circle at 20% 10%, rgba(244,124,60,0.16), transparent 24%), radial-gradient(circle at 80% 12%, rgba(30,47,99,0.14), transparent 24%), radial-gradient(circle at 50% 92%, rgba(141,31,39,0.12), transparent 28%)',
      }}
    />

    <Stack spacing={0.85} alignItems="center" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
      <motion.div
        animate={{ y: [0, -4, 0], scale: [1, 1.03, 1] }}
        transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            background: 'rgba(255,255,255,0.82)',
            boxShadow: `0 10px 22px ${alpha('#059669', 0.08)}`,
            position: 'relative',
            border: `1px solid ${alpha('#059669', 0.08)}`,
          }}
        >
          <SchoolRoundedIcon sx={{ fontSize: 26, color: '#059669' }} />
        </Box>
      </motion.div>

      <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 760, letterSpacing: 0.08 }}>
        {label}
      </Typography>
      <Typography sx={{ color: '#64748B', fontSize: '0.84rem', maxWidth: 360, lineHeight: 1.55 }}>
        Preparing your school experience with academics, campus life, and updates.
      </Typography>
      <CircularProgress size={16} thickness={5} sx={{ color: '#059669' }} />
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.2 }}>
        {[0, 1, 2].map((index) => (
          <Box
            key={`loader-bar-${index}`}
            component={motion.div}
            animate={{ opacity: [0.35, 1, 0.35], scaleX: [0.88, 1, 0.88] }}
            transition={{ repeat: Infinity, duration: 1.1, delay: index * 0.18 }}
            sx={{
              width: 18,
              height: 3,
              borderRadius: 2,
              bgcolor: alpha('#059669', 0.55),
              transformOrigin: 'center',
            }}
          />
        ))}
      </Stack>
    </Stack>
  </Box>
);

export const circularImageSx = {
  width: 58,
  height: 58,
  borderRadius: 0,
  objectFit: 'cover',
  border: `2px solid ${alpha('#059669', 0.25)}`,
};

export const iconBubbleSx = {
  width: 34,
  height: 34,
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  border: `1px solid ${alpha('#059669', 0.24)}`,
  boxShadow: `0 0 0 3px ${alpha('#059669', 0.10)}`,
  color: '#059669',
  background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
};

export const HeroWave = ({ color = '#FFFFFF', height = 58 }) => (
  <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: -1, lineHeight: 0, zIndex: 3 }}>
    <svg
      viewBox="0 0 1440 120"
      preserveAspectRatio="none"
      style={{ width: '100%', height: `${height}px`, display: 'block' }}
    >
      <path
        fill={color}
        d="M0,54 C170,98 355,8 540,42 C735,78 900,24 1088,46 C1240,64 1340,44 1440,30 L1440,120 L0,120 Z"
      />
    </svg>
  </Box>
);

const mediaCurvePresets = [
  {
    xs: 0,
    md: 0,
  },
  {
    xs: 0,
    md: 0,
  },
  {
    xs: 0,
    md: 0,
  },
  {
    xs: 0,
    md: 0,
  },
  {
    xs: 0,
    md: 0,
  },
  {
    xs: 0,
    md: 0,
  },
];

export const createOrganicMediaFrameSx = (waveColor = '#FFFFFF', variant = 0) => {
  const selected = mediaCurvePresets[Math.abs(Number(variant) || 0) % mediaCurvePresets.length];
  return {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: {
      xs: selected.xs,
      md: selected.md,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: '-6%',
      right: '-6%',
      bottom: -1,
      height: { xs: 14, md: 18 },
      background: waveColor,
      borderRadius: 0,
      pointerEvents: 'none',
    },
  };
};

export const isDirectVideoUrl = (value = '') =>
  typeof value === 'string' && /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(value.trim());

export const WEB_LAYOUT = {
  heroMinHeight: { xs: '44vh', md: '52vh' },
  heroContentPy: { xs: 4.8, md: 6.2 },
  sectionPy: { xs: 1.1, md: 1.8 },
};

export const InlineEditActions = ({ visible = false, onEdit, tone = 'hero' }) => {
  if (!visible) {
    return null;
  }

  const isSurface = tone === 'surface';
  const color = '#FFFFFF';
  const bgColor = isSurface ? '#0F172A' : alpha('#0F172A', 0.9);
  const hoverBgColor = '#0B1220';
  const ringColor = isSurface ? alpha('#059669', 0.28) : alpha('#FFFFFF', 0.38);

  return (
    <Stack direction="row" spacing={0.65} flexWrap="wrap" useFlexGap>
      <Button
        size="medium"
        variant="contained"
        startIcon={<EditOutlinedIcon sx={{ fontSize: 17 }} />}
        onClick={onEdit}
        sx={{
          color,
          bgcolor: bgColor,
          fontWeight: 780,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          boxShadow: `0 0 0 1px ${ringColor}`,
          border: `1px solid ${ringColor}`,
          '&:hover': { bgcolor: hoverBgColor, boxShadow: `0 0 0 1px ${ringColor}` },
        }}
      >
        Edit Mode
      </Button>
    </Stack>
  );
};
